/**
 * Step8_Review.js
 * Read-only summary of all collected data + submit button.
 * On Submit → POST /requests/{id}/submit/
 */

import { useState, useEffect, useCallback } from 'react';
import { submitRequest, getMatches } from '../services/propertyRequestService';
import GEO_FEATURE_MAPPING from '../geoFeatureMapping';

/* ── MatchResults component ── */
function MatchResults({ requestId }) {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState('');
    const [page, setPage]       = useState(1);

    const load = useCallback(async (p) => {
        setLoading(true);
        setError('');
        try {
            const res = await getMatches(requestId, p, 10);
            setData(res);
        } catch {
            setError('خطا در بارگذاری نتایج. لطفاً دوباره تلاش کنید.');
        } finally {
            setLoading(false);
        }
    }, [requestId]);

    useEffect(() => { load(page); }, [load, page]);

    if (loading) return (
        <div className="s8-matches-loading">
            <span className="s8-spinner" />
            <p>در حال جستجوی بهترین ملک‌ها...</p>
        </div>
    );

    if (error) return (
        <div className="s8-matches-error">
            <p>{error}</p>
            <button className="prw-btn prw-btn-secondary" onClick={() => load(page)}>تلاش مجدد</button>
        </div>
    );

    if (!data?.results?.length) return (
        <div className="s8-matches-empty">
            <span style={{ fontSize: 40 }}>🔍</span>
            <p>ملکی منطبق با شرایط شما یافت نشد. می‌توانید شرایط خود را ویرایش کنید.</p>
        </div>
    );

    return (
        <div className="s8-matches">
            <p className="s8-matches-count">{data.total} ملک منطبق یافت شد</p>
            <div className="s8-matches-list">
                {data.results.map((item) => (
                    <div key={item.listing_id} className="s8-match-card">
                        <div className="s8-match-score">
                            <span className="s8-match-score-val">{Math.round(item.score * 100)}%</span>
                            <span className="s8-match-score-label">تطابق</span>
                        </div>
                        <div className="s8-match-info">
                            <div className="s8-match-type">
                                {item.listing_type === 'RENT' ? '🔑 اجاره' : '🏠 فروش'}
                                {item.neighborhood_name && (
                                    <span className="s8-match-neighborhood"> · {item.neighborhood_name}</span>
                                )}
                            </div>
                            <div className="s8-match-price">
                                {item.listing_type === 'RENT' ? (
                                    <>
                                        {item.monthly_rent && <span>اجاره: {Number(item.monthly_rent).toLocaleString('fa-IR')} تومان</span>}
                                        {item.min_deposit  && <span> | ودیعه: {Number(item.min_deposit).toLocaleString('fa-IR')} تومان</span>}
                                    </>
                                ) : (
                                    item.asking_price && <span>قیمت: {Number(item.asking_price).toLocaleString('fa-IR')} تومان</span>
                                )}
                            </div>
                            {item.area && <div className="s8-match-area">متراژ: {item.area} م²</div>}
                        </div>
                    </div>
                ))}
            </div>
            {data.total > 10 && (
                <div className="s8-matches-pagination">
                    <button
                        className="prw-btn prw-btn-secondary"
                        disabled={page === 1}
                        onClick={() => setPage(p => p - 1)}
                    >قبلی</button>
                    <span>صفحه {page}</span>
                    <button
                        className="prw-btn prw-btn-secondary"
                        disabled={data.results.length < 10}
                        onClick={() => setPage(p => p + 1)}
                    >بعدی</button>
                </div>
            )}
        </div>
    );
}

const INTENT_LABELS = { BUY: 'خرید', RENT: 'اجاره', PRESALE: 'پیش‌خرید', COLAB: 'مشارکت' };
const PET_LABELS    = { cat: 'گربه', dog: 'سگ', bird: 'پرنده' };
const fmt = (v) => v ? Number(v).toLocaleString('fa-IR') : '—';

/* ── small helpers ── */
const Row = ({ label, value }) =>
    value ? (
        <div className="s8-row">
            <span className="s8-label">{label}</span>
            <span className="s8-value">{value}</span>
        </div>
    ) : null;

/* Collapsible section card */
function Section({ title, icon, children, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="s8-card">
            <button
                type="button"
                className={`s8-card-header ${open ? 's8-card-open' : ''}`}
                onClick={() => setOpen(o => !o)}
            >
                <span className="s8-card-icon">{icon}</span>
                <span className="s8-card-title">{title}</span>
                <span className="s8-card-chevron">{open ? '▲' : '▼'}</span>
            </button>
            {open && <div className="s8-card-body">{children}</div>}
        </div>
    );
}

/* Ranked chip list for amenities / geo features */
function RankedList({ items, labelKey, requiredKey = 'required' }) {
    if (!items?.length) return <span className="s8-empty">انتخاب نشده</span>;
    return (
        <ol className="s8-ranked-list">
            {items.map((item, i) => (
                <li key={i} className="s8-ranked-item">
                    <span className="s8-ranked-num">{i + 1}</span>
                    <span className="s8-ranked-label">{item[labelKey]}</span>
                    {item[requiredKey] && <span className="s8-req-badge">الزامی</span>}
                </li>
            ))}
        </ol>
    );
}

/* Resolve geo feature question text from mapping */
function geoLabel(item) {
    const mapping = GEO_FEATURE_MAPPING[item.geo_feature];
    if (!mapping) return `ویژگی #${item.geo_feature}`;
    // Find the matching option label for the stored value
    const opt = mapping.options.find(o => o.value === item.value);
    return mapping.question.replace('؟', '') + (opt ? ` — ${opt.label}` : '');
}

export default function Step8_Review({ requestId, draftData, onBack, onDone }) {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted]   = useState(false);
    const [error, setError]           = useState('');

    const d = draftData;

    const handleSubmit = async () => {
        setError('');
        setSubmitting(true);
        try {
            await submitRequest(requestId);
            setSubmitted(true);
        } catch (err) {
            const msg = err?.response?.data?.detail || 'خطا در ارسال درخواست. لطفاً دوباره تلاش کنید.';
            setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="s8-submitted">
                <div className="s8-success-banner">
                    <div className="s8-success-icon">✓</div>
                    <div>
                        <h2 className="prw-step-title">درخواست شما ثبت شد!</h2>
                        <p className="prw-step-desc">بهترین ملک‌های منطبق با درخواست شما:</p>
                    </div>
                </div>
                <MatchResults requestId={requestId} />
                <div className="prw-nav" style={{ marginTop: 24 }}>
                    <button className="prw-btn prw-btn-primary" onClick={onDone}>
                        بازگشت به صفحه اصلی
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <h2 className="prw-step-title">بررسی و ارسال</h2>
            <p className="prw-step-desc">
                اطلاعات وارد شده را مرور کنید. برای ویرایش هر بخش روی آن کلیک کنید تا باز شود.
            </p>

            <div className="s8-review-body">

                <Section title="هدف و نوع ملک" icon="🏠">
                    <Row label="نوع معامله" value={INTENT_LABELS[d.intent] || d.intent} />
                    <Row label="نوع ملک"    value={d.property_type_label || d.property_type} />
                    <Row label="کاربری"     value={d.usage_label || d.usage} />
                </Section>

                <Section title="بودجه" icon="">
                    {d.intent === 'RENT' ? (
                        <>
                            <Row label="ودیعه ایده‌آل"  value={d.ideal_deposit      ? `${fmt(d.ideal_deposit)} تومان`       : null} />
                            <Row label="اجاره ایده‌آل"  value={d.ideal_rent         ? `${fmt(d.ideal_rent)} تومان/ماه`      : null} />
                            <Row label="بودجه کل معادل" value={d.total_budget       ? `${fmt(d.total_budget)} تومان`        : null} />
                            <Row label="سقف ودیعه"      value={d.deposit_budget_max ? `${fmt(d.deposit_budget_max)} تومان`  : null} />
                            <Row label="سقف اجاره"      value={d.rent_budget_max    ? `${fmt(d.rent_budget_max)} تومان/ماه` : null} />
                        </>
                    ) : (
                        <Row label="حداکثر بودجه خرید" value={d.purchase_budget_max ? `${fmt(d.purchase_budget_max)} تومان` : null} />
                    )}
                </Section>

                <Section title="مشخصات ملک" icon="📐">
                    <Row label="حداقل متراژ"   value={d.min_area_sqm      ? `${d.min_area_sqm} م²`       : null} />
                    <Row label="حداکثر متراژ"  value={d.max_area_sqm      ? `${d.max_area_sqm} م²`       : null} />
                    <Row label="حداکثر سن بنا" value={d.max_property_age  ? `${d.max_property_age} سال`  : null} />
                    <Row label="نوع سند"        value={d.deed_type && d.deed_type !== 'NONE' ? d.deed_type : null} />
                </Section>

                <Section title="امکانات" icon="✨" defaultOpen={false}>
                    <RankedList items={d.amenities} labelKey="amenity_label" />
                </Section>

                <Section title="ترجیحات محیطی" icon="🌳" defaultOpen={false}>
                    {d.geo_features?.length ? (
                        <ol className="s8-ranked-list">
                            {d.geo_features.map((item, i) => (
                                <li key={i} className="s8-ranked-item">
                                    <span className="s8-ranked-num">{i + 1}</span>
                                    <span className="s8-ranked-label">{geoLabel(item)}</span>
                                    {item.required && <span className="s8-req-badge">الزامی</span>}
                                </li>
                            ))}
                        </ol>
                    ) : <span className="s8-empty">انتخاب نشده</span>}
                </Section>

                <Section title="محله‌های مورد نظر" icon="📍" defaultOpen={false}>
                    {d.neighborhoods?.length ? (
                        <div className="s8-chips">
                            {d.neighborhoods.map((n, i) => (
                                <span key={i} className="s8-chip">{n.name || n}</span>
                            ))}
                        </div>
                    ) : <span className="s8-empty">انتخاب نشده</span>}
                </Section>

                <Section title="خانوار" icon="👨‍👩‍👧">
                    <Row label="بزرگسال"      value={d.adults_count   != null ? `${d.adults_count} نفر`   : null} />
                    <Row label="کودک"          value={d.children_count != null ? `${d.children_count} نفر` : null} />
                    <Row label="حیوان خانگی"   value={PET_LABELS[d.pet] || (d.pet && d.pet !== 'none' ? d.pet : 'ندارم')} />
                </Section>

            </div>

            {error && <p className="prw-error">{error}</p>}

            <div className="prw-nav">
                <button className="prw-btn prw-btn-secondary" onClick={onBack} disabled={submitting}>
                    → مرحله قبل
                </button>
                <button
                    className="prw-btn prw-btn-submit"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? 'در حال ارسال...' : 'ارسال درخواست ✓'}
                </button>
            </div>
        </div>
    );
}
